// The planner call behind the new-agent box (POST /api/agents/plan/), with a time limit of its own.
//
// The planner is one blocking call to the model: usually a few seconds, up to about 70 s (a 10 s
// connect and a 60 s read timeout; p5_agent_views_contract.md). authedFetch has no timeout, so the
// request is raced against PLAN_TIMEOUT_MS: past it the box stops waiting and says so, and Retry asks
// again. Asking again is safe, since planning saves nothing.
//
// The request that ran out of time still runs on the server and may answer later. Every request is
// numbered, and only the newest one's answer or failure is ever shown; a cancel or an unmount retires
// the newest one too.
//
// request(body) resolves with the planner's answer (an object), or with null: when it failed (`error`
// says why), ran out of time, or was retired by a newer request, a cancel or an unmount. It never
// rejects. `error` is {text, retry}: a sentence (agentErrorText), and whether Retry is worth offering
// (a passing failure: the time limit, no answer at all or a gateway's error page, an answer with no
// plan in it, or a 503 that isn't "switched off"). `cancel()` stops waiting and clears the error.
// lostAnswer(err) and NO_CONNECTION are the chat's too.
import { useCallback, useEffect, useRef, useState } from 'react';
import { planAgent } from './agentsService';
import { agentErrorCanRetry, agentErrorText } from './agentErrors';

export const PLAN_TIMEOUT_MS = 90 * 1000;
export const TOO_LONG = 'Planning took too long. Try again.';
export const NO_CONNECTION = "Couldn't reach NotesAI just now. Try again.";
export const NO_PLAN = "The planner didn't send a plan back. Try again.";

// A failure that asking again may well fix, with the words to show for it.
class Passing extends Error {}

const hasSentence = (err) => typeof err?.payload?.error === 'string' && err.payload.error.trim() !== '';
// Whether a request got no answer to go by, so it may or may not have reached the server: fetch's own
// failure (a TypeError: authedFetch's own errors are plain Errors with a status), or a 5xx with no JSON
// sentence (a gateway's own page: a 502, a 504 timeout). The chat and the cards use it too.
export function lostAnswer(err) {
  if (err?.name === 'TypeError') return true;
  return Number.isInteger(err?.status) && err.status >= 500 && !hasSentence(err);
}

function describe(err) {
  if (err instanceof Passing) return { text: err.message, retry: true };
  if (lostAnswer(err)) return { text: NO_CONNECTION, retry: true };
  return { text: agentErrorText(err), retry: agentErrorCanRetry(err) };
}

export default function usePlanRequest() {
  const [planning, setPlanning] = useState(false);
  const [error, setError] = useState(null);
  const newest = useRef(0);

  // Unmounted: whatever is still waiting lands nowhere.
  useEffect(() => () => { newest.current += 1; }, []);

  const request = useCallback(async (body) => {
    newest.current += 1;
    const mine = newest.current;
    setPlanning(true);
    setError(null);
    let timer = null;
    const timeLimit = new Promise((resolve, reject) => {
      timer = setTimeout(() => reject(new Passing(TOO_LONG)), PLAN_TIMEOUT_MS);
    });
    let answer = null;
    let failure = null;
    try {
      answer = await Promise.race([planAgent(body), timeLimit]);
      if (!answer || typeof answer !== 'object') failure = new Passing(NO_PLAN);
    } catch (err) {
      failure = err || new Error('');
    } finally {
      clearTimeout(timer);
    }
    if (mine !== newest.current) return null;
    setPlanning(false);
    if (failure) {
      setError(describe(failure));
      return null;
    }
    return answer;
  }, []);

  const cancel = useCallback(() => {
    newest.current += 1;
    setPlanning(false);
    setError(null);
  }, []);

  return { planning, error, request, cancel };
}
